import { useMutation, useQuery } from 'react-query';
import {
  getWorkorderConfigurationCompany,
  listWorkorderConfigurationProfile,
  updateWorkorderConfigurationCompany,
  updateWorkorderConfigurationProfile,
} from './WorkordersConfiguration.service';
import { WorkorderConfigurationCompany } from './WorkordersConfiguration.types';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';
import { WorkordersConfigurationErrors } from './WorkordersConfiguration.errors';
import { queryClient } from '@/Root';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function useWorkorderConfigurationProfile() {
  const cache = queryClient.getQueryData(['workorder/config/profile']);

  return useQuery(
    ['workorder/config/profile'],
    () => {
      return listWorkorderConfigurationProfile();
    },
    {
      enabled: !cache,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useUpdateWorkorderConfigurationProfile() {
  const queryChanel = useQueryChanel();

  return useMutation(updateWorkorderConfigurationProfile, {
    async onSuccess() {
      queryChanel({
        refetchQueries: [['workOrders/list']],
        removeQueries: [['workorder/config/profile']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkorderConfigurationCompany() {
  return useMutation(
    async (companyId: number) => {
      const cache = queryClient.getQueryData<WorkorderConfigurationCompany>([
        'workorder/config/company',
        companyId,
      ]);

      if (!!cache) return cache;

      return getWorkorderConfigurationCompany(companyId);
    },
    {
      onSuccess(data, companyId) {
        queryClient.setQueryData(['workorder/config/company', companyId], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useWorkorderConfigurationCompanyQuery(
  companyId?: number,
  enabled = true,
) {
  const cache = queryClient.getQueryData<WorkorderConfigurationCompany>([
    'workorder/config/company',
    companyId,
  ]);

  return useQuery(
    ['workorder/config/company', companyId],
    () => getWorkorderConfigurationCompany(companyId),
    {
      enabled: !!companyId && !cache && enabled,
      retry: 0,
    },
  );
}

export function useUpdateWorkorderConfigurationCompany() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    ({
      companyId,
      obj,
    }: {
      companyId: number;
      obj: WorkorderConfigurationCompany;
    }) => updateWorkorderConfigurationCompany(companyId, obj),
    {
      onSuccess(_, vars) {
        notification.success({
          message: intl.formatMessage({
            id: 'settings.workorderConfiguration.update.success',
          }),
          description: intl.formatMessage({
            id: 'settings.workorderConfiguration.update.success.description',
          }),
        });

        queryChanel({
          resetQueries: [
            ['workorder/config/profile'],
            ['workorder/config/company', vars.companyId],
          ],
        });
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => WorkordersConfigurationErrors[err]),
        });
      },
    },
  );

  return mutation;
}
